<script lang="ts" setup>
import { computed, PropType } from "vue";
import { TaskParameterTypeEnum } from "./enums";
import { TaskParameterModel } from "./models";
import {
  DifficultyParameter,
  PriorityParameter,
  StatusParameter,
  TagParameter,
  TypeParameter,
} from "./components";

/**Параметры */
const props = defineProps({
  /**Данные о настройке */
  data: { type: Object as PropType<TaskParameterModel>, default: () => new TaskParameterModel() },
  /**Тип настройки */
  type: { type: Number as PropType<TaskParameterTypeEnum> },
  /**Демонстрационный тег */
  preview: { type: Boolean, default: false },
  /**Убрать кнопку удаления */
  hideRemove: { type: Boolean, default: false },
  /**Редактирование */
  isEditing: { type: Boolean, default: false },
  /**Укороченная информация параметра */
  shortInfoParameter: { type: Boolean, default: false },
});
/**События */
const emit = defineEmits<{
  /**Удаление тега */
  (e: "remove", name: string);
  /**Изменение тега */
  (e: "upsert", extension: TaskParameterModel): void;
}>();

/**Компонент для отображения параметра */
const parameterComponent = computed(() => {
  switch (props.type) {
    case TaskParameterTypeEnum.Status:
      return StatusParameter;
    case TaskParameterTypeEnum.Priority:
      return PriorityParameter;
    case TaskParameterTypeEnum.Tag:
      return TagParameter;
    case TaskParameterTypeEnum.Difficulty:
      return DifficultyParameter;
    case TaskParameterTypeEnum.Type:
      return TypeParameter;
  }
});

/**Класс бейджа */
const parameterClass = computed(() => {
  const classes = [];
  switch (props.type) {
    case TaskParameterTypeEnum.Status:
      classes.push("parameter-status");
      break;
    case TaskParameterTypeEnum.Priority:
      classes.push("parameter-priority");
      break;
    case TaskParameterTypeEnum.Tag:
      classes.push("parameter-tag");
      break;
    case TaskParameterTypeEnum.Difficulty:
      classes.push("parameter-difficulty");
      break;
    case TaskParameterTypeEnum.Type:
      classes.push("parameter-type");
      break;
  }

  return classes;
});
/**Имя типа настройки */
const typeText = computed(() => {
  switch (props.type) {
    case TaskParameterTypeEnum.Status:
      return "статуса";
    case TaskParameterTypeEnum.Priority:
      return "приоритета";
    case TaskParameterTypeEnum.Tag:
      return "тега";
    case TaskParameterTypeEnum.Difficulty:
      return "сложности";
    case TaskParameterTypeEnum.Type:
      return "типа";
  }
});

/**Событие удаления тега */
const remove = () => emit("remove", props.data.Name);
</script>
<template>
  <div class="task-parameter-wrapper" :class="parameterClass">
    <component
      :is="parameterComponent"
      :isEditing="isEditing"
      :data="props.data"
      :type="props.type"
      :shortInfoParameter="shortInfoParameter"
      :hide-remove="hideRemove || preview"
      :preview="preview"
      @remove="remove"
    />
  </div>
</template>
<style lang="scss" scoped>
.task-parameter {
  &_settings {
    display: flex;
    flex-direction: column;

    .tir-popover_element {
      width: 100%;
    }
  }

  &_list-item {
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    height: 3rem;
    padding: 0 0.75rem;
    transition: 0.1s ease;
    cursor: pointer;

    &:hover {
      background-color: var(--neutral-translucent-5);
    }

    &_arrow {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: 0.75rem;
      width: 1rem;
      height: 1rem;
      transition: 0.3s ease;

      &.rotate {
        transform: rotate(180deg);
      }
    }

    &_icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      cursor: pointer;
      border-radius: 0.25rem;
      transition: 0.1s ease;

      &:hover {
        background-color: var(--neutral-translucent-5);
      }

      & > svg {
        width: 1.25rem;
        height: 1.25rem;
      }
    }
  }

  &_edit-name {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
  }
}
</style>
